// ABOUTME: Mobile optimization utilities for responsive padding and spacing across BePono app
// ABOUTME: Provides centralized values and helper functions for consistent mobile layout optimization

import { SxProps, Theme } from '@mui/material/styles';

/**
 * Standard padding values for mobile optimization
 * Based on Material-UI spacing scale (8px base unit)
 */
export const MOBILE_PADDING = {
  // Card content padding - aggressive reduction from 16px to 8px
  CARD_CONTENT: {
    xs: 1,    // 8px on extra small screens (phones)
    sm: 1,    // 8px on small screens (large phones/small tablets)
    md: 2,    // 16px on medium screens and up (standard Material-UI default)
  },

  // Page-level container padding - aggressive reduction from 24px to 8px
  PAGE_CONTAINER: {
    xs: 1,    // 8px on extra small screens - maximum space utilization
    sm: 1,    // 8px on small screens - maximum space utilization
    md: 3,    // 24px on medium screens and up (original value)
  },

  // Spacing between major sections - aggressive reduction on mobile
  SECTION_SPACING: {
    xs: 1.5,  // 12px - aggressive reduction for mobile space optimization
    sm: 1.5,  // 12px - same for small tablets
    md: 3,    // 24px - maintain on larger screens for visual hierarchy
  },
} as const;

/**
 * Chart margin configurations for mobile optimization
 * Focuses on left/right margin reduction while preserving readability
 */
export const MOBILE_CHART_MARGINS = {
  // Standard chart margins (bar charts, line charts, etc.)
  STANDARD: {
    mobile: { top: 10, right: 8, left: 5, bottom: 5 },
    desktop: { top: 20, right: 30, left: 20, bottom: 5 },
  },

  // Time-based charts — minimal bottom margin; X-axis labels render inside the plot area
  TIME_CHART: {
    mobile: { top: 10, right: 8, left: 5, bottom: 5 },
    desktop: { top: 20, right: 30, left: 20, bottom: 5 },
  },

  // Time-based charts with rotated X-axis labels (angle=-45) that need extra bottom clearance
  ROTATED_TIME_CHART: {
    mobile: { top: 10, right: 8, left: 5, bottom: 20 },
    desktop: { top: 20, right: 30, left: 20, bottom: 35 },
  },

  // Composed charts (like PositiveNegative behaviors)
  COMPOSED_CHART: {
    mobile: { top: 10, right: 5, left: 5, bottom: 5 },
    desktop: { top: 20, right: 30, left: 20, bottom: 5 },
  },

  // Pie charts typically don't use margins, rely on container padding
  PIE_CHART: {
    mobile: undefined,
    desktop: undefined,
  },
} as const;

/**
 * Helper function to create responsive card content padding
 * Returns SxProps object for Material-UI components
 */
export const createMobileCardContentSx = (additionalSx?: SxProps<Theme>): SxProps<Theme> => {
  const basePadding: SxProps<Theme> = {
    p: MOBILE_PADDING.CARD_CONTENT,
  };

  if (additionalSx) {
    return { ...basePadding, ...additionalSx };
  }

  return basePadding;
};

/**
 * Helper function to create responsive page container padding
 * Returns SxProps object for main page containers
 */
export const createMobilePageContainerSx = (additionalSx?: SxProps<Theme>): SxProps<Theme> => {
  const basePadding: SxProps<Theme> = {
    p: MOBILE_PADDING.PAGE_CONTAINER,
  };

  if (additionalSx) {
    return { ...basePadding, ...additionalSx };
  }

  return basePadding;
};

/**
 * Helper function to create responsive section spacing
 * Returns SxProps object for spacing between major sections
 */
export const createMobileSectionSpacingSx = (additionalSx?: SxProps<Theme>): SxProps<Theme> => {
  const baseSpacing: SxProps<Theme> = {
    gap: MOBILE_PADDING.SECTION_SPACING,
  };

  if (additionalSx) {
    return { ...baseSpacing, ...additionalSx };
  }

  return baseSpacing;
};

/**
 * Hook-like function to get appropriate chart margins based on screen size
 * Use with useTheme hook and breakpoints for responsive behavior
 */
export const getMobileChartMargins = (
  chartType: 'standard' | 'timeChart' | 'rotatedTimeChart' | 'composedChart' | 'pieChart',
  isMobile: boolean
) => {
  const marginConfig = MOBILE_CHART_MARGINS[
    chartType === 'standard' ? 'STANDARD' :
    chartType === 'timeChart' ? 'TIME_CHART' :
    chartType === 'rotatedTimeChart' ? 'ROTATED_TIME_CHART' :
    chartType === 'composedChart' ? 'COMPOSED_CHART' :
    'PIE_CHART'
  ];

  return isMobile ? marginConfig.mobile : marginConfig.desktop;
};

/**
 * Utility to determine if current breakpoint should use mobile optimizations
 * Use with Material-UI's useMediaQuery hook
 */
export const MOBILE_BREAKPOINT_QUERY = '(max-width:899px)'; // xs and sm (under md)

/**
 * Type definitions for better TypeScript support
 */
export type MobileCardContentSx = SxProps<Theme>;
export type MobilePageContainerSx = SxProps<Theme>;
export type MobileSectionSpacingSx = SxProps<Theme>;
export type ChartMarginConfig = {
  top: number;
  right: number;
  left: number;
  bottom: number;
} | undefined;